import type { NavItem } from '../components/BottomNav'
import { IconDashboard, IconDownload, IconFitnessCenter, IconFlag, IconInfo, IconMenuBook, IconPerson, IconSettings, IconTrendingUp } from '../components/icons'

/**
 * The web's information architecture (redesign.md §3, §5): settings groups, library tabs and the old paths that now
 * redirect, kept apart from the router so they can be tested and so the nav and the pages agree on the names.
 */

export const SETTINGS_GROUPS = [
  { id: 'profile', label: 'Profile & goals' },
  { id: 'nutrition', label: 'Nutrition & coaching' },
  { id: 'training', label: 'Training' },
  { id: 'data', label: 'Data & integrations' },
  { id: 'app', label: 'App' },
  { id: 'advanced', label: 'Advanced' },
] as const
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]['id']

export function isSettingsGroup(value: string): value is SettingsGroup {
  return SETTINGS_GROUPS.some((group) => group.id === value)
}

export const LIBRARY_TABS = [
  { id: 'foods', label: 'Foods' },
  { id: 'custom', label: 'Custom foods' },
  { id: 'recipes', label: 'Recipes' },
  { id: 'meals', label: 'Meals' },
] as const
export type LibraryTab = (typeof LIBRARY_TABS)[number]['id']

/** The `tab` search param, defaulting to Foods for a missing or unknown value. */
export function parseLibraryTab(value: unknown): LibraryTab {
  return LIBRARY_TABS.find((tab) => tab.id === value)?.id ?? 'foods'
}

/** Old pages that moved into the Library, so bookmarks keep working. */
export const LIBRARY_REDIRECTS = {
  '/foods': 'foods',
  '/custom-foods': 'custom',
  '/recipes': 'recipes',
  '/meals': 'meals',
} as const satisfies Readonly<Record<string, LibraryTab>>

/** The id in an editor URL that means "a new one" rather than a saved item. */
export const NEW_ID = 'new'

/**
 * Whether a nav link is the current page: an exact match or a page under it (`/library/recipes/…` under `/library`,
 * `/settings/nutrition` under `/settings`), but not a sibling that only shares a prefix (`/log` vs `/logout`).
 */
export function isActivePath(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * The five destinations phone and web share (redesign.md §3): Today, Diary, Train, Progress, Plan, icon and label
 * always shown. This one list drives both the side rail (wide screens) and the bottom bar (narrow), so the rail and
 * the bar can never drift apart (`Layout.tsx`) — unlike the old rail, each destination is a single hub page; the
 * pages it used to list as separate rail items (Weight trend, Expenditure, Workouts, …) are reached from inside that
 * hub instead (`ProgressPage.tsx`, `TrainPage.tsx`), so their own URLs are unchanged and still bookmark.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/today', label: 'Today', icon: IconDashboard },
  { to: '/diary', label: 'Diary', icon: IconMenuBook },
  { to: '/train', label: 'Train', icon: IconFitnessCenter },
  { to: '/progress', label: 'Progress', icon: IconTrendingUp },
  { to: '/plan', label: 'Plan', icon: IconFlag },
]

/** The bottom bar's own items (phones): the same five destinations, nothing extra (redesign.md §3's "You" is a
 * header button on every breakpoint, not a sixth tab, so there is no "More" catch-all to add here any more). */
export const BOTTOM_NAV_ITEMS: readonly NavItem[] = NAV_ITEMS

/** The "You" button at the top right of every page (redesign.md §3): settings, account, help, about and
 * import/export, never a nav-list item — kept here only so its icon and label have one source like the rest. */
export const YOU_NAV_ITEM: NavItem = { to: '/you', label: 'You', icon: IconPerson }

/** `YouPage.tsx`'s own links, replacing the old header `AccountMenu` dropdown and the bottom nav's "More" page. */
export const YOU_LINKS: readonly NavItem[] = [
  { to: '/settings', label: 'Settings', icon: IconSettings },
  { to: '/about', label: 'About', icon: IconInfo },
  { to: '/export/download', label: 'Export my data', icon: IconDownload },
]

/** The bottom nav's own left-to-right order (compact windows). */
export const BOTTOM_NAV_ORDER: readonly string[] = NAV_ITEMS.map((item) => item.to)

/** The rail's own top-to-bottom order (600 px and up). Identical to `BOTTOM_NAV_ORDER` now that both surfaces show
 * the same five destinations in the same order (ponytail: one array would do, but the two names document which
 * surface a caller means and cost nothing to keep separate). */
export const RAIL_ORDER: readonly string[] = NAV_ITEMS.map((item) => item.to)

export type NavDirection = 'forward' | 'backward' | 'none'

/**
 * The slide direction for a route change (owner override, 24 Oct 2026, replacing fade-through; `router.tsx`'s
 * `defaultViewTransition`, styles.css's `:active-view-transition-type()` rules). [order] is whichever nav surface
 * is current — `BOTTOM_NAV_ORDER` on phones, `RAIL_ORDER` from 600 px. A destination switch slides by its position in
 * that order: further along slides in from the right/below, earlier from the left/above. Anything else is "deeper"
 * navigation, judged by path containment: a page nested under the current one is forward (right), returning to an
 * ancestor is backward (left, "back reversing the direction") — an unrelated jump (neither a surface switch nor an
 * ancestor/descendant, including a jump to "You", which sits outside this order on purpose) defaults forward.
 */
export function navDirection(order: readonly string[], from: string, to: string): NavDirection {
  if (from === to) return 'none'
  const fromIndex = order.indexOf(from)
  const toIndex = order.indexOf(to)
  if (fromIndex !== -1 && toIndex !== -1) return toIndex > fromIndex ? 'forward' : 'backward'
  if (from.startsWith(`${to}/`)) return 'backward'
  return 'forward'
}
